import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Search, FileText, Send, Ban, Eye, Download, Pencil, Trash2, ReceiptText } from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-muted text-muted-foreground',
  issued: 'bg-blue-100 text-blue-700',
  applied: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const INITIAL_FORM = {
  invoice_id: '',
  credit_note_date: new Date().toISOString().split('T')[0],
  base_amount: '',
  gst_type: 'igst',
  reason: 'Candidate left before 90-day guarantee period',
  line_items: [{ _key: 1, description: 'Manpower Recruitment and supply agency services', candidate_name: '', date_of_joining: '', amount: '' }],
  sac_code: '998519',
  lut_arn_no: 'AD0603240203210',
  lut_arn_date: '12/03/2024',
  manual_igst: '',
  manual_cgst: '',
  manual_sgst: '',
  round_off: '0',
  is_manual_entry: false,
  manual_credit_note_number: '',
};

export default function CreditNotesPage() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewCreditNote, setViewCreditNote] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [useManualGST, setUseManualGST] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cnRes, invRes] = await Promise.all([
        api.get('/finance/credit-notes'),
        api.get('/finance/invoices'),
      ]);
      setCreditNotes(cnRes.data);
      setInvoices(invRes.data.filter(inv => inv.status !== 'cancelled'));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => creditNotes.filter(cn => {
    const matchSearch = cn.credit_note_number?.toLowerCase().includes(search.toLowerCase()) ||
                        cn.client_name?.toLowerCase().includes(search.toLowerCase()) ||
                        cn.invoice_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || cn.status === statusFilter;
    return matchSearch && matchStatus;
  }), [creditNotes, search, statusFilter]);

  const openAdd = () => {
    setForm(INITIAL_FORM);
    setEditMode(false);
    setEditingId(null);
    setUseManualGST(false);
    setDialogOpen(true);
  };

  const openEdit = async (cn) => {
    try {
      const res = await api.get(`/finance/credit-notes/${cn.id}`);
      const data = res.data;
      
      setForm({
        invoice_id: data.invoice_id,
        credit_note_date: data.credit_note_date,
        base_amount: data.base_amount.toString(),
        gst_type: data.gst_type,
        reason: data.reason,
        line_items: data.line_items?.length > 0 ? data.line_items.map((item, idx) => ({ ...item, _key: idx + 1 })) : INITIAL_FORM.line_items,
        sac_code: data.sac_code || '998519',
        lut_arn_no: data.lut_arn_no || 'AD0603240203210',
        lut_arn_date: data.lut_arn_date || '12/03/2024',
        manual_igst: data.igst?.toString() || '',
        manual_cgst: data.cgst?.toString() || '',
        manual_sgst: data.sgst?.toString() || '',
        round_off: data.round_off?.toString() || '0',
        is_manual_entry: data.is_manual_entry,
        manual_credit_note_number: data.credit_note_number,
      });
      
      setUseManualGST(data.is_manual_entry);
      setEditMode(true);
      setEditingId(data.id);
      setDialogOpen(true);
    } catch {
      toast.error('Failed to load credit note details');
    }
  };

  const handleInvoiceChange = (invoiceId) => {
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (invoice) {
      setForm(prev => ({
        ...prev,
        invoice_id: invoiceId,
        gst_type: invoice.gst_type || 'igst',
      }));
    }
  };

  const updateLineItem = (index, field, value) => {
    setForm(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      const totalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      return {
        ...prev,
        line_items: newItems,
        base_amount: totalAmount.toString(),
      };
    });
  };

  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { _key: Date.now(), description: 'Manpower Recruitment and supply agency services', candidate_name: '', date_of_joining: '', amount: '' }],
    }));
  };

  const removeLineItem = (index) => {
    if (form.line_items.length === 1) return;
    setForm(prev => {
      const newItems = prev.line_items.filter((_, i) => i !== index);
      const totalAmount = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      return {
        ...prev,
        line_items: newItems,
        base_amount: totalAmount.toString(),
      };
    });
  };

  const handleSave = async () => {
    if (!form.invoice_id || !form.base_amount || !form.credit_note_date) {
      toast.error('Please fill all required fields');
      return;
    }
    if (form.is_manual_entry && !form.manual_credit_note_number) {
      toast.error('Credit note number is required for manual entry');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_amount: parseFloat(form.base_amount),
        line_items: form.line_items.map((item, idx) => ({
          description: item.description,
          candidate_name: item.candidate_name || null,
          date_of_joining: item.date_of_joining || null,
          quantity: 1,
          amount: parseFloat(item.amount) || 0,
        })),
        round_off: parseFloat(form.round_off) || 0,
      };
      
      if (useManualGST || form.is_manual_entry) {
        if (form.manual_igst) payload.manual_igst = parseFloat(form.manual_igst);
        if (form.manual_cgst) payload.manual_cgst = parseFloat(form.manual_cgst);
        if (form.manual_sgst) payload.manual_sgst = parseFloat(form.manual_sgst);
      }
      
      if (editMode) {
        await api.put(`/finance/credit-notes/${editingId}`, {
          credit_note_number: form.manual_credit_note_number,
          credit_note_date: form.credit_note_date,
          base_amount: parseFloat(form.base_amount),
          gst_type: form.gst_type,
          reason: form.reason,
          line_items: payload.line_items,
          sac_code: form.sac_code,
          lut_arn_no: form.lut_arn_no,
          lut_arn_date: form.lut_arn_date,
          round_off: payload.round_off,
          ...(useManualGST && {
            manual_igst: parseFloat(form.manual_igst) || undefined,
            manual_cgst: parseFloat(form.manual_cgst) || undefined,
            manual_sgst: parseFloat(form.manual_sgst) || undefined,
          }),
        });
        toast.success('Credit note updated');
      } else {
        await api.post('/finance/credit-notes', payload);
        toast.success('Credit note created');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credit note');
    } finally {
      setSaving(false);
    }
  };

  const issueCreditNote = async (cnId) => {
    try {
      await api.put(`/finance/credit-notes/${cnId}/issue`);
      toast.success('Credit note issued');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue');
    }
  };

  const cancelCreditNote = async (cnId) => {
    if (!window.confirm('Are you sure you want to cancel this credit note?')) return;
    try {
      await api.put(`/finance/credit-notes/${cnId}/cancel`);
      toast.success('Credit note cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const viewDetails = async (cnId) => {
    try {
      const res = await api.get(`/finance/credit-notes/${cnId}`);
      setViewCreditNote(res.data);
    } catch {
      toast.error('Failed to load credit note');
    }
  };

  const downloadPdf = async (cnId, cnNumber) => {
    try {
      const res = await api.get(`/finance/credit-notes/${cnId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CreditNote_${cnNumber.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN')}`;
  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Calculate totals for preview
  const baseAmount = parseFloat(form.base_amount) || 0;
  const roundOff = parseFloat(form.round_off) || 0;
  let gstAmount = 0;
  let igstVal = 0, cgstVal = 0, sgstVal = 0;
  
  if (useManualGST || form.is_manual_entry) {
    igstVal = parseFloat(form.manual_igst) || 0;
    cgstVal = parseFloat(form.manual_cgst) || 0;
    sgstVal = parseFloat(form.manual_sgst) || 0;
    gstAmount = igstVal + cgstVal + sgstVal;
  } else {
    gstAmount = baseAmount * 0.18;
    if (form.gst_type === 'igst') {
      igstVal = gstAmount;
    } else {
      cgstVal = baseAmount * 0.09;
      sgstVal = baseAmount * 0.09;
    }
  }
  const totalAmount = baseAmount + gstAmount + roundOff;

  return (
    <div data-testid="credit-notes-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Credit Notes</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">Manage credit notes for candidates who left before guarantee period</p>
        </div>
        <Button onClick={openAdd} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="add-credit-note-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Credit Note
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search credit notes..." className="pl-10 rounded-sm" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="applied">Applied</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest">Credit Note #</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Invoice #</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Amount</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No credit notes found</TableCell></TableRow>
            ) : filtered.map(cn => (
              <TableRow key={cn.id} data-testid={`credit-note-row-${cn.id}`}>
                <TableCell className="font-mono text-sm font-medium text-red-600">{cn.credit_note_number}</TableCell>
                <TableCell className="font-mono text-sm">{cn.invoice_number}</TableCell>
                <TableCell>{cn.client_name}</TableCell>
                <TableCell className="text-sm">{cn.credit_note_date}</TableCell>
                <TableCell className="text-right font-mono text-red-600">{formatCurrency(cn.total_amount)}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[cn.status]}>{cn.status}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewDetails(cn.id)} title="View">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(cn.id, cn.credit_note_number)} title="Download PDF">
                      <Download className="w-4 h-4" />
                    </Button>
                    {!['issued', 'applied', 'cancelled'].includes(cn.status) && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(cn)} title="Edit" className="text-blue-600">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {cn.status === 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => issueCreditNote(cn.id)} className="text-emerald-600" title="Issue">
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {!['applied', 'cancelled'].includes(cn.status) && (
                      <Button variant="ghost" size="sm" onClick={() => cancelCreditNote(cn.id)} className="text-red-600" title="Cancel">
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Credit Note Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <ReceiptText className="w-5 h-5" />
              {editMode ? 'Edit Credit Note' : 'Create Credit Note'}
            </DialogTitle>
            <DialogDescription>For candidates who left before 90-day guarantee period</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {/* Manual Entry Toggle */}
            <div className="flex items-center gap-2 py-2 border-b border-border">
              <Switch checked={form.is_manual_entry} onCheckedChange={v => { set('is_manual_entry', v); setUseManualGST(v); }} />
              <span className="text-sm text-muted-foreground">Manual entry (for previously raised credit notes)</span>
            </div>

            {/* Credit Note Number for Manual Entry */}
            {(form.is_manual_entry || editMode) && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Credit Note Number *</Label>
                <Input value={form.manual_credit_note_number} onChange={e => set('manual_credit_note_number', e.target.value)} className="rounded-sm font-mono" placeholder="CRN/001/24-25" />
              </div>
            )}

            {/* Invoice Selection */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Original Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={handleInvoiceChange} disabled={editMode}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {invoices.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} - {inv.client_name} ({formatCurrency(inv.total_amount)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Credit Note Date */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Credit Note Date *</Label>
              <Input type="date" value={form.credit_note_date} onChange={e => set('credit_note_date', e.target.value)} className="rounded-sm" />
            </div>

            {/* SAC Code and LUT Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">SAC Code</Label>
                <Input value={form.sac_code} onChange={e => set('sac_code', e.target.value)} className="rounded-sm font-mono" placeholder="998519" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">LUT ARN No.</Label>
                <Input value={form.lut_arn_no} onChange={e => set('lut_arn_no', e.target.value)} className="rounded-sm font-mono" placeholder="AD0603240203210" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">LUT Date</Label>
                <Input value={form.lut_arn_date} onChange={e => set('lut_arn_date', e.target.value)} className="rounded-sm" placeholder="12/03/2024" />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="rounded-sm">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="border border-border rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 dark:bg-red-950">
                    <tr>
                      <th className="p-2 text-left text-xs uppercase w-16">S.No</th>
                      <th className="p-2 text-left text-xs uppercase">Description</th>
                      <th className="p-2 text-left text-xs uppercase">Candidate Name</th>
                      <th className="p-2 text-left text-xs uppercase">DOJ</th>
                      <th className="p-2 text-right text-xs uppercase">Total</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.line_items.map((item, idx) => (
                      <tr key={item._key || idx} className="border-t border-border">
                        <td className="p-1 text-center font-mono text-muted-foreground">{idx + 1}</td>
                        <td className="p-1">
                          <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} className="rounded-sm" placeholder="Manpower Recruitment..." />
                        </td>
                        <td className="p-1">
                          <Input value={item.candidate_name} onChange={e => updateLineItem(idx, 'candidate_name', e.target.value)} className="rounded-sm" placeholder="Candidate name" />
                        </td>
                        <td className="p-1">
                          <Input type="date" value={item.date_of_joining} onChange={e => updateLineItem(idx, 'date_of_joining', e.target.value)} className="rounded-sm" />
                        </td>
                        <td className="p-1">
                          <Input type="number" value={item.amount} onChange={e => updateLineItem(idx, 'amount', e.target.value)} className="rounded-sm text-right font-mono" placeholder="0" />
                        </td>
                        <td className="p-1">
                          {form.line_items.length > 1 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* GST Options */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div className="space-y-1.5 flex-1">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">GST Type</Label>
                  <Select value={form.gst_type} onValueChange={v => set('gst_type', v)} disabled={useManualGST}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="igst">IGST (18%) - Inter-state</SelectItem>
                      <SelectItem value="cgst_sgst">CGST + SGST (9% + 9%) - Haryana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={useManualGST} onCheckedChange={setUseManualGST} />
                  <span className="text-xs text-muted-foreground">Manual GST</span>
                </div>
              </div>

              {useManualGST && (
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-sm">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">IGST Amount</Label>
                    <Input type="number" value={form.manual_igst} onChange={e => set('manual_igst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">CGST Amount</Label>
                    <Input type="number" value={form.manual_cgst} onChange={e => set('manual_cgst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">SGST Amount</Label>
                    <Input type="number" value={form.manual_sgst} onChange={e => set('manual_sgst', e.target.value)} className="rounded-sm font-mono" placeholder="0" />
                  </div>
                </div>
              )}
            </div>

            {/* Round Off */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Round Off</Label>
              <Input type="number" value={form.round_off} onChange={e => set('round_off', e.target.value)} className="rounded-sm font-mono w-40" placeholder="0" step="0.01" />
            </div>

            {/* Amount Summary */}
            {baseAmount > 0 && (
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-sm text-sm space-y-2 border border-red-200 dark:border-red-800">
                <div className="flex justify-between"><span>Subtotal:</span><span className="font-mono">{formatCurrency(baseAmount)}</span></div>
                {igstVal > 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>IGST (18%):</span><span className="font-mono">{formatCurrency(igstVal)}</span></div>
                )}
                {cgstVal > 0 && (
                  <>
                    <div className="flex justify-between text-muted-foreground"><span>CGST (9%):</span><span className="font-mono">{formatCurrency(cgstVal)}</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>SGST (9%):</span><span className="font-mono">{formatCurrency(sgstVal)}</span></div>
                  </>
                )}
                {roundOff !== 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>Round Off:</span><span className="font-mono">{formatCurrency(roundOff)}</span></div>
                )}
                <div className="flex justify-between font-medium border-t border-red-300 dark:border-red-700 pt-2 text-base text-red-600">
                  <span>Total Credit:</span>
                  <span className="font-mono">{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason for Credit Note</Label>
              <Textarea value={form.reason} onChange={e => set('reason', e.target.value)} className="rounded-sm" rows={2} placeholder="Candidate left before 90-day guarantee period" />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 rounded-sm">
              {saving ? 'Saving...' : editMode ? 'Update Credit Note' : 'Create Credit Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Credit Note Dialog */}
      <Dialog open={!!viewCreditNote} onOpenChange={() => setViewCreditNote(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <ReceiptText className="w-5 h-5" />
              Credit Note {viewCreditNote?.credit_note_number}
            </DialogTitle>
          </DialogHeader>
          {viewCreditNote && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Client</p>
                  <p className="font-medium">{viewCreditNote.client?.client_name}</p>
                  <p className="text-sm text-muted-foreground">{viewCreditNote.client?.billing_address}</p>
                  <p className="text-sm font-mono">{viewCreditNote.client?.gst_number}</p>
                </div>
                <div className="space-y-2 text-right">
                  <Badge className={STATUS_COLORS[viewCreditNote.status]}>{viewCreditNote.status}</Badge>
                  <p className="text-sm"><span className="text-muted-foreground">Date:</span> {viewCreditNote.credit_note_date}</p>
                  <p className="text-sm"><span className="text-muted-foreground">Invoice:</span> {viewCreditNote.invoice?.invoice_number}</p>
                  {viewCreditNote.sac_code && <p className="text-sm font-mono"><span className="text-muted-foreground">SAC:</span> {viewCreditNote.sac_code}</p>}
                </div>
              </div>

              {viewCreditNote.line_items?.length > 0 && (
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 dark:bg-red-950">
                      <tr>
                        <th className="p-2 text-left w-16">S.No</th>
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-left">Candidate Name</th>
                        <th className="p-2 text-left">DOJ</th>
                        <th className="p-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewCreditNote.line_items.map((item, idx) => (
                        <tr key={item._key || `view-${idx}`} className="border-t border-border">
                          <td className="p-2 text-center font-mono">{idx + 1}</td>
                          <td className="p-2">{item.description}</td>
                          <td className="p-2">{item.candidate_name}</td>
                          <td className="p-2">{item.date_of_joining}</td>
                          <td className="p-2 text-right font-mono">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="border-t border-b border-border py-4">
                <p className="text-sm"><span className="text-muted-foreground">Reason:</span> {viewCreditNote.reason}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Base Amount</span><span className="font-mono">{formatCurrency(viewCreditNote.base_amount)}</span></div>
                {viewCreditNote.igst > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">IGST (18%)</span><span className="font-mono">{formatCurrency(viewCreditNote.igst)}</span></div>
                )}
                {viewCreditNote.cgst > 0 && (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">CGST (9%)</span><span className="font-mono">{formatCurrency(viewCreditNote.cgst)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">SGST (9%)</span><span className="font-mono">{formatCurrency(viewCreditNote.sgst)}</span></div>
                  </>
                )}
                {viewCreditNote.round_off !== 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Round Off</span><span className="font-mono">{formatCurrency(viewCreditNote.round_off)}</span></div>
                )}
                <div className="flex justify-between font-medium border-t border-border pt-2 text-red-600">
                  <span>Total Credit</span>
                  <span className="font-mono text-lg">{formatCurrency(viewCreditNote.total_amount)}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => downloadPdf(viewCreditNote.id, viewCreditNote.credit_note_number)} className="rounded-sm">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
